import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useRef, useState } from "react";
import { useEditorDependencyInjectionContext } from "../providers";
import { FileUploader } from "../FileUploader";
import { SButton, SDropdownV3, SField, SIcon, SModalV2, STextarea, SVariablePicker, SCheckboxV2 } from "../..";
import { SInputV2 } from "../../SInputV2";
import { SWStateRecordType } from "@superwall-me/schema";
import { IoCut, IoImage, IoSparkles } from "react-icons/io5";
import { STYLE_OPTIONS, SUBSTYLE_OPTIONS } from "./helpers/recraft";
import SContextMenu from "../../SContextMenu";
export const ImagePropertyEditor = ({ readOnly, value, onChangeValue, onClickValue })=>{
    const uploadContext = useEditorDependencyInjectionContext();
    const aiContext = useEditorDependencyInjectionContext();
    const { useVariablePickerItems, requiresLiquid, getRequiredPaths } = useEditorDependencyInjectionContext();
    const items = useVariablePickerItems();
    const [hasFocus, setHasFocus] = useState(false);
    const inputRef = useRef(null);
    const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
    const [description, setDescription] = useState("");
    const [style, setStyle] = useState(STYLE_OPTIONS[0].value);
    const [subStyle, setSubStyle] = useState(SUBSTYLE_OPTIONS[style][0].value);
    const [autoRemoveBackground, setAutoRemoveBackground] = useState(false);
    // const [inputValue, setInputValue] = useState(props.src)
    const [selectedFile, setSelectedFile] = useState();
    const { mutateAsync: upload, isError, error, isLoading, reset: resetUploadFn } = uploadContext.useUploadFile();
    const [processingStatus, setProcessingStatus] = useState(null);
    const { mutateAsync: generateImage, isError: isGenerateImageError } = aiContext.useGenerateImage();
    const { mutateAsync: removeBackground, isError: isRemoveBackgroundError } = aiContext.useRemoveBackground();
    const defaultPlaceholder = "e.g. http://example.com/image.jpg";
    const placeholder = defaultPlaceholder;
    const _onChange = useCallback((src)=>{
        if (requiresLiquid(src)) {
            const requiredRecords = getRequiredPaths(src).map((x)=>SWStateRecordType.createId(x));
            onChangeValue && onChangeValue({
                type: "property-image",
                ...value,
                src: src,
                rendering: {
                    type: "liquid",
                    requiredStateIds: requiredRecords
                }
            });
        } else {
            onChangeValue && onChangeValue({
                type: "property-image",
                ...value,
                src: src,
                rendering: {
                    type: "literal"
                }
            });
        }
    }, [
        onChangeValue,
        value
    ]);
    const uploadImage = async (file)=>{
        setSelectedFile(file);
        try {
            const resp = await upload({
                file
            });
            const { url } = resp.result;
            _onChange(url);
        } catch (e) {}
    };
    const onHandleUpload = async (file)=>{
        setSelectedFile(file);
        await uploadImage(file);
    };
    const onRetryUpload = async (e)=>{
        e.preventDefault();
        await uploadImage(selectedFile);
    };
    const onInputChange = (e)=>{
        e.preventDefault();
        _onChange(e.target.value);
        if (isError) resetUploadFn();
    };
    function dataURLtoFile(dataurl, filename) {
        const arr = dataurl.split(",");
        const mime = arr[0].match(/:(.*?);/)?.[1];
        const bstr = atob(arr[arr.length - 1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while(n--){
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([
            u8arr
        ], filename, {
            type: mime
        });
    }
    const onGenerateImage = async ()=>{
        try {
            // Generate initial image
            setProcessingStatus("generating");
            const { dataUrl } = await generateImage({
                prompt: description,
                style,
                subStyle
            });
            let file = dataURLtoFile(dataUrl, `generated-${Math.random().toString(36).substring(7)}.png`);
            setProcessingStatus("uploading");
            const { result: { url: uploadedUrl } } = await upload({
                file
            });
            _onChange(uploadedUrl);
            // Remove background if enabled
            if (autoRemoveBackground) {
                setProcessingStatus("removing");
                const { dataUrl: noBackgroundUrl } = await removeBackground({
                    imageUrl: uploadedUrl
                });
                file = dataURLtoFile(noBackgroundUrl, `no-background-${Math.random().toString(36).substring(7)}.png`);
                setProcessingStatus("uploading");
                const { result: { url: finalUrl } } = await upload({
                    file
                });
                _onChange(finalUrl);
                setIsAIMenuOpen(false);
            }
        } catch  {
            setProcessingStatus(null);
        } finally{
            setProcessingStatus(null);
        }
    };
    const onRemoveBackground = async ()=>{
        try {
            setProcessingStatus("removing");
            const { dataUrl } = await removeBackground({
                imageUrl: value?.src || ""
            });
            const file = dataURLtoFile(dataUrl, `no-background-${Math.random().toString(36).substring(7)}.png`);
            setProcessingStatus("uploading");
            await onHandleUpload(file);
        } catch  {
            setProcessingStatus(null);
        } finally{
            setProcessingStatus(null);
        }
    };
    // context menu
    const [isAIMenuOpen, setIsAIMenuOpen] = useState(false);
    // Converts images with relative paths to includes the paywall url base url, including the release version
    return /*#__PURE__*/ _jsx("div", {
        onClick: onClickValue,
        children: /*#__PURE__*/ _jsx("div", {
            className: readOnly ? "pointer-events-none" : "",
            children: /*#__PURE__*/ _jsxs(SField, {
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "grid grid-cols-[1fr,auto] items-center gap-2 focus:bg-red-300",
                        children: [
                            /*#__PURE__*/ _jsx(SInputV2, {
                                value: value?.src || "",
                                onChange: onInputChange,
                                placeholder: placeholder,
                                ref: inputRef,
                                onFocus: ()=>setHasFocus(true),
                                onBlur: ()=>setTimeout(()=>{
                                        setHasFocus(false);
                                    }, 1000)
                            }),
                            /*#__PURE__*/ _jsx(FileUploader, {
                                ariaLabel: "Image Upload",
                                handleFile: onHandleUpload,
                                hideText: true,
                                hasError: !!error,
                                isSuiButton: true,
                                suiButtonProps: {
                                    variant: "primary",
                                    className: "h-[32px] w-[32px] border border-brand-primary/7 flex items-center justify-center !rounded-md !py-0 "
                                },
                                handleRetry: onRetryUpload,
                                isLoading: isLoading
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-col pt-1.5 pr-1 pl-0 pb-0 group items-start",
                        children: [
                            value?.src && !value?.src.startsWith("https://user-content.superwalleditor.com/") && /*#__PURE__*/ _jsx("p", {
                                className: "text-sm opacity-50",
                                children: "External image URLs may impact performance and incur CDN costs. For best results, upload images directly to Superwall."
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center w-full justify-between gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx(SContextMenu, {
                                        isOpen: isAIMenuOpen,
                                        setIsOpen: setIsAIMenuOpen,
                                        children: /*#__PURE__*/ _jsx(SContextMenu.Open, {
                                            isMinimal: true,
                                            menu: /*#__PURE__*/ _jsx(SContextMenu.Menu, {
                                                width: 200,
                                                direction: "left",
                                                children: /*#__PURE__*/ _jsxs("div", {
                                                    className: "bg-grayscale-dark rounded-md shadow-xl border border-white/10",
                                                    children: [
                                                        /*#__PURE__*/ _jsxs("button", {
                                                            className: "group flex w-full items-center px-4 py-2 text-sm text-white/95 gap-2 hover:bg-grayscale-medium",
                                                            onClick: ()=>{
                                                                setIsAIMenuOpen(false);
                                                                setIsGenerateModalOpen(true);
                                                            },
                                                            children: [
                                                                /*#__PURE__*/ _jsx(IoImage, {
                                                                    size: 14,
                                                                    className: "text-brand-primary"
                                                                }),
                                                                "Generate Image"
                                                            ]
                                                        }),
                                                        /*#__PURE__*/ _jsxs("button", {
                                                            className: "group flex w-full items-center px-4 py-2 text-sm text-white/95 gap-2 hover:bg-grayscale-medium",
                                                            onClick: ()=>{
                                                                setIsAIMenuOpen(false);
                                                                onRemoveBackground();
                                                            },
                                                            children: [
                                                                /*#__PURE__*/ _jsx(IoCut, {
                                                                    size: 14,
                                                                    className: "text-brand-primary -rotate-45"
                                                                }),
                                                                "Remove Background"
                                                            ]
                                                        })
                                                    ]
                                                }, "SVariablePicker")
                                            }),
                                            className: "!h-[24px] !py-0 !pl-0",
                                            disabled: processingStatus !== null,
                                            children: processingStatus ? /*#__PURE__*/ _jsxs(_Fragment, {
                                                children: [
                                                    /*#__PURE__*/ _jsx(SIcon, {
                                                        type: "spinner",
                                                        className: "animate-spin",
                                                        size: 14
                                                    }),
                                                    processingStatus === "generating" && "Generating image...",
                                                    processingStatus === "uploading" && "Uploading image...",
                                                    processingStatus === "removing" && "Removing background..."
                                                ]
                                            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                                children: [
                                                    /*#__PURE__*/ _jsx(IoSparkles, {
                                                        size: 12
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "text-sm",
                                                        children: "AI Tools"
                                                    })
                                                ]
                                            })
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(SModalV2, {
                                        title: "Generate Image",
                                        exitable: true,
                                        actions: /*#__PURE__*/ _jsxs("div", {
                                            className: "flex justify-between items-center w-full gap-10",
                                            children: [
                                                /*#__PURE__*/ _jsx(SCheckboxV2, {
                                                    value: autoRemoveBackground,
                                                    onChange: setAutoRemoveBackground,
                                                    checkSize: 18,
                                                    className: "text-sm text-white/70",
                                                    children: "Remove background automatically"
                                                }),
                                                /*#__PURE__*/ _jsx(SButton, {
                                                    onClick: async ()=>{
                                                        setIsGenerateModalOpen(false);
                                                        await onGenerateImage();
                                                    },
                                                    disabled: processingStatus !== null,
                                                    children: "Generate"
                                                })
                                            ]
                                        }),
                                        isOpen: isGenerateModalOpen,
                                        onClose: ()=>setIsGenerateModalOpen(false),
                                        children: /*#__PURE__*/ _jsxs("div", {
                                            className: "flex flex-col gap-4 items-start max-w-[450px] pt-4 pb-6",
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "w-full",
                                                    children: [
                                                        /*#__PURE__*/ _jsx(SField.Label, {
                                                            children: "Description"
                                                        }),
                                                        /*#__PURE__*/ _jsx(STextarea, {
                                                            className: "!my-0 !text-sm !resize-none w-full !bg-grayscale-dark",
                                                            placeholder: "Enter a description of the image you want to generate...",
                                                            value: description,
                                                            onChange: (e)=>setDescription(e.target.value)
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "grid grid-cols-2 gap-4 w-full",
                                                    children: [
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            children: [
                                                                /*#__PURE__*/ _jsx(SField.Label, {
                                                                    children: "Style"
                                                                }),
                                                                /*#__PURE__*/ _jsx(SDropdownV3, {
                                                                    borderless: false,
                                                                    placeholder: style,
                                                                    className: "!h-[32px] !text-[13px] !py-0 !px-2 bg-grayscale-dark",
                                                                    value: style,
                                                                    onChange: (value)=>{
                                                                        setStyle(value.value);
                                                                        setSubStyle(SUBSTYLE_OPTIONS[value.value][0].value) // reset when style changes
                                                                        ;
                                                                    },
                                                                    options: STYLE_OPTIONS
                                                                })
                                                            ]
                                                        }),
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            children: [
                                                                /*#__PURE__*/ _jsx(SField.Label, {
                                                                    children: "Sub-style"
                                                                }),
                                                                /*#__PURE__*/ _jsx(SDropdownV3, {
                                                                    borderless: false,
                                                                    placeholder: subStyle,
                                                                    className: "!h-[32px] !text-[13px] !py-0 !px-2 bg-grayscale-dark",
                                                                    value: subStyle,
                                                                    onChange: (value)=>setSubStyle(value.value),
                                                                    options: SUBSTYLE_OPTIONS[style] || []
                                                                })
                                                            ]
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(SVariablePicker, {
                                        items: items,
                                        asButton: true,
                                        enableHotkey: hasFocus,
                                        didSelect: (item)=>{
                                            let text = value?.src || "";
                                            if (inputRef.current) {
                                                const { selectionStart, selectionEnd } = inputRef.current;
                                                const beforeText = text.substring(0, selectionStart || 0).trimEnd();
                                                const textToInsert = item.liquid?.trim() || "";
                                                const rhs = text.substring(selectionEnd || 0, text.length).trimStart();
                                                const lhs = `${beforeText}${textToInsert}`;
                                                const newPosition = lhs.length;
                                                text = `${lhs}${rhs}`;
                                                _onChange(text);
                                                setTimeout(()=>{
                                                    inputRef.current?.focus();
                                                    if (inputRef.current) {
                                                        inputRef.current.selectionStart = newPosition;
                                                        inputRef.current.selectionEnd = newPosition;
                                                    }
                                                }, 100);
                                            } else {
                                                _onChange(`${value?.src || ""}${item.liquid?.trim()}`);
                                            }
                                        },
                                        didClose: ()=>{
                                            setTimeout(()=>{
                                                inputRef.current?.focus();
                                            }, 100);
                                        }
                                    })
                                ]
                            }),
                            isError && /*#__PURE__*/ _jsx("div", {
                                className: "text-red-400 text-sm font-medium",
                                children: error?.message
                            }),
                            isGenerateImageError && /*#__PURE__*/ _jsx("div", {
                                className: "text-red-400 text-sm font-medium",
                                children: "Could not generate image, please try again."
                            }),
                            isRemoveBackgroundError && /*#__PURE__*/ _jsx("div", {
                                className: "text-red-400 text-sm font-medium",
                                children: "Could not remove background, please try again."
                            })
                        ]
                    })
                ]
            })
        })
    });
};
